import { useState } from "react";
import Chart from "react-apexcharts";
import { fmtBRL, fmtInt } from "@/lib/api";
import { apexBase, useChartTheme, PALETTE } from "@/components/production/pcShared";
import { Panel, Empty } from "./biShared";

const GRANS = [["day", "Dia"], ["week", "Semana"], ["month", "Mês"]];

export default function BiOverviewTab({ data }) {
  const dark = useChartTheme();
  const [gran, setGran] = useState("day");
  const base = apexBase(dark);
  const serie = data.timeseries?.[gran] || [];
  const weekday = data.weekday || [];
  const hourly = data.hourly || [];
  const stores = data.by_store || [];

  const evoOpts = {
    ...base,
    chart: { ...base.chart, type: "line", zoom: { enabled: false } },
    stroke: { curve: "smooth", width: [3, 2, 0] },
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 2 } },
    xaxis: { categories: serie.map((r) => r.key), labels: { rotate: -45, style: { fontSize: "10px" } } },
    yaxis: [
      { seriesName: ["Bruto", "Líquido"], title: { text: "R$" }, labels: { formatter: (v) => fmtBRL(v) } },
      { seriesName: "Pedidos", opposite: true, title: { text: "Pedidos" }, labels: { formatter: (v) => fmtInt(v) } },
    ],
    tooltip: { ...base.tooltip, shared: true, intersect: false },
    colors: [PALETTE[0], PALETTE[2], PALETTE[1]],
  };

  return (
    <div className="space-y-3" data-testid="bi-overview-tab">
      <Panel title="EVOLUÇÃO DO FATURAMENTO" testid="bi-chart-evolution"
        right={
          <div className="flex gap-1">
            {GRANS.map(([k, l]) => (
              <button key={k} onClick={() => setGran(k)}
                className={`text-[10px] px-2 py-0.5 border rounded ${gran === k ? "bg-black text-white border-black" : "border-zinc-300"}`}
                data-testid={`bi-gran-${k}`}>{l}</button>
            ))}
          </div>
        }>
        {serie.length === 0 ? <Empty /> : (
          <Chart type="line" height={300}
            series={[
              { name: "Bruto", type: "line", data: serie.map((r) => r.gross) },
              { name: "Líquido", type: "line", data: serie.map((r) => r.net) },
              { name: "Pedidos", type: "column", data: serie.map((r) => r.orders) },
            ]}
            options={evoOpts} />
        )}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="FATURAMENTO POR DIA DA SEMANA" testid="bi-chart-weekday">
          {weekday.every((w) => w.orders === 0) ? <Empty /> : (
            <Chart type="bar" height={240}
              series={[{ name: "Bruto", data: weekday.map((w) => w.gross) }]}
              options={{
                ...base,
                plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
                xaxis: { categories: weekday.map((w) => w.label) },
                yaxis: { labels: { formatter: (v) => fmtBRL(v) } },
                tooltip: {
                  ...base.tooltip,
                  y: { formatter: (v, { dataPointIndex }) => `${fmtBRL(v)} · ${fmtInt(weekday[dataPointIndex]?.orders || 0)} pedidos` },
                },
                colors: [PALETTE[0]],
              }} />
          )}
        </Panel>
        <Panel title="PARTICIPAÇÃO POR LOJA (BRUTO)" testid="bi-chart-store-share">
          {stores.length === 0 ? <Empty /> : (
            <Chart type="donut" height={240}
              series={stores.map((s) => s.gross)}
              options={{
                ...base,
                labels: stores.map((s) => s.store),
                legend: { position: "bottom", fontSize: "10px" },
                dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
                tooltip: { ...base.tooltip, y: { formatter: (v) => fmtBRL(v) } },
              }} />
          )}
        </Panel>
      </div>

      <Panel title="PEDIDOS POR HORA DE IMPORTAÇÃO" testid="bi-chart-hourly"
        note="⚠ O Bling não envia a hora exata da venda — este gráfico usa a hora em que o pedido foi importado pelo sistema.">
        {hourly.every((h) => h.orders === 0) ? <Empty /> : (
          <Chart type="bar" height={220}
            series={[{ name: "Pedidos", data: hourly.map((h) => h.orders) }]}
            options={{
              ...base,
              plotOptions: { bar: { borderRadius: 2, columnWidth: "70%" } },
              xaxis: { categories: hourly.map((h) => `${h.hour}h`), labels: { style: { fontSize: "9px" } } },
              tooltip: {
                ...base.tooltip,
                y: { formatter: (v, { dataPointIndex }) => `${fmtInt(v)} pedidos · ${fmtBRL(hourly[dataPointIndex]?.gross || 0)}` },
              },
              colors: [PALETTE[4]],
            }} />
        )}
      </Panel>
    </div>
  );
}
